import { useEffect, useState } from "react";
import { billingService, setupService } from "../../../../Core/Api/services";
import {
  Alert,
  Autocomplete,
  Button,
  InputLabel,
  Link,
  OutlinedInput,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { PlatformInfos } from "../../../../Core/Api/Types/platform-infos";
import { produce } from "immer";
import OutlinedCheckbox from "../../../../Components/Forms/OutlinedCheckbox";
import { CollectionToCopy } from "../../../../Core/Api/Types/collection-to-copy";
import { CloudProvider } from "../../../../Core/Api/Types/cloud-provider";
import { CopyPlatformRequest } from "../../../../Core/Api/Types/copy-platform-request";
import GlobalConst from "../../../../Core/Utils/Constants/globals";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { DbSubscriptionOfferInfos } from "../../../../Core/Api/Types/db-subscription-offer-infos";
import { useTranslation } from "react-i18next";

function AdminSettingsCopyPlatform() {
  const enqueueSnackbar = useCustomSnackbar();
  const { t } = useTranslation();

  const [platforms, setPlatforms] = useState<PlatformInfos[]>([]);
  const [collectionToCopyList, setCollectionToCopyList] = useState<CollectionToCopy[]>([]);
  const [offers, setOffers] = useState<DbSubscriptionOfferInfos[]>([]);
  const [rootGroupName, setRootGroupName] = useState<string>("ROOT");
  const [rootUserLogin, setRootUserLogin] = useState<string>("admin");
  const [rootUserEmail, setRootUserEmail] = useState<string>("");
  const [rootUserPassword, setRootUserPassword] = useState<string>("");
  const [platformName, setPlatformName] = useState<string>("");
  const [platformBaseUrl, setPlatformBaseUrl] = useState<string>(GlobalConst.URL_APP);
  const [platformLoginSuffix, setPlatformLoginSuffix] = useState<string>("");
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const [request, setRequest] = useState<{
    sourcePlatformId: number;
    specifiedNewPlatformId: number | null;
    duplicateResources: boolean;
    cloudProvider: CloudProvider | null;
    collectionsToCopy: CollectionToCopy[];
    billingOfferId: number | null;
    copyUserGroups: boolean;
  }>({
    sourcePlatformId: 0,
    specifiedNewPlatformId: null,
    duplicateResources: false,
    cloudProvider: null,
    collectionsToCopy: [],
    billingOfferId: null,
    copyUserGroups: true,
  });

  const handleValidateClick = () => {
    var copyPlatformRequest: CopyPlatformRequest = new CopyPlatformRequest({
      sourcePlatformId: request.sourcePlatformId,
      rootGroupName: rootGroupName,
      rootUserLogin: rootUserLogin,
      rootUserEmail: rootUserEmail,
      rootUserPassword: rootUserPassword,
      platformName: platformName,
      platformBaseUrl: platformBaseUrl,
      platformLoginSuffix: platformLoginSuffix,
      specifiedNewPlatformId: request.specifiedNewPlatformId,
      duplicateResources: request.duplicateResources,
      cloudProvider: request.cloudProvider,
      collectionsToCopy: request.collectionsToCopy,
      billingOfferId: request.billingOfferId,
      copyUserGroups: request.copyUserGroups,
    });

    setIsSubmitLoading(true);

    setupService.CopyPlatform(copyPlatformRequest).then((response) => {
      setIsSubmitLoading(false);

      if (!response.success) enqueueSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
      else enqueueSnackbar(t("alert:success.platformSetupSuccessfully"), "success");
    });
  };

  const isValid = () => {
    if (request.sourcePlatformId === undefined || request.sourcePlatformId === null || request.sourcePlatformId === 0)
      return false;
    else if (rootGroupName === undefined || rootGroupName === null || rootGroupName === "") return false;
    else if (rootUserLogin === undefined || rootUserLogin === null || rootUserLogin === "") return false;
    else if (rootUserPassword === undefined || rootUserPassword === null || rootUserPassword === "") return false;
    else if (platformName === undefined || platformName === null || platformName === "") return false;
    else if (platformBaseUrl === undefined || platformBaseUrl === null || platformBaseUrl === "") return false;
    else if (platformLoginSuffix === undefined || platformLoginSuffix === null || platformLoginSuffix === "")
      return false;
    return true;
  };

  useEffect(() => {
    setupService.ListPlatforms().then((response) => {
      if (response.platforms) {
        setPlatforms(response.platforms);
      }
    });

    setupService.GetPlatformInfosEnums().then((response) => {
      if (response.collectionsToCopy) {
        setCollectionToCopyList(response.collectionsToCopy);
        setRequest(
          produce((draft) => {
            draft.collectionsToCopy = response.collectionsToCopy as CollectionToCopy[];
          })
        );
      }
    });

    billingService.ListSubscriptionOffers().then((response) => {
      if (response && response.offers) setOffers(response.offers);
    });
  }, []);

  return (
    <Stack sx={{ flex: "1 1 0%", overflowY: "scroll", py: 2 }}>
      <Typography variant="h3" sx={{ mb: 3 }}>
        {t("common:platformCopy")}
      </Typography>

      <InputLabel sx={{ mb: 1 }}>{t("field:label.platformSource")}</InputLabel>
      <Autocomplete
        value={platforms.find((x) => x.id === request.sourcePlatformId) || null}
        onChange={(e, value) =>
          setRequest(
            produce((draft) => {
              draft.sourcePlatformId = value!.id as number;
            })
          )
        }
        options={platforms}
        getOptionLabel={(option: any) => option.name}
        sx={{ width: "100%", mb: 2 }}
        renderInput={(params) => (
          <TextField sx={{ mt: "-24px" }} {...params} placeholder={t("field:placeholder.selectPlatformAsModel")} />
        )}
      />

      <InputLabel sx={{ mb: 1 }}>{t("field:label.rootGroupName")}</InputLabel>
      <OutlinedInput
        value={rootGroupName}
        id={"name"}
        sx={{ width: "100%", mb: 2 }}
        placeholder={t("field:placeholder.rootGroupNameShouldBeRoot")}
        onChange={(e) => setRootGroupName(e.currentTarget.value)}
      />

      <InputLabel sx={{ mb: 1 }}>{t("field:label.rootUserLogin")}</InputLabel>
      <OutlinedInput
        value={rootUserLogin}
        id={"name"}
        sx={{ width: "100%", mb: 2 }}
        placeholder={t("field:placeholder.loginExcludingSuffix")}
        onChange={(e) => setRootUserLogin(e.currentTarget.value)}
      />

      <InputLabel sx={{ mb: 1 }}>{t("field:label.rootUserPassword")}</InputLabel>
      <OutlinedInput
        value={rootUserPassword}
        id={"name"}
        sx={{ width: "100%", mb: 2 }}
        placeholder={t("field:placeholder.useStrongPassword")}
        onChange={(e) => setRootUserPassword(e.currentTarget.value)}
      />

      <InputLabel sx={{ mb: 1 }}>{t("field:label.rootUserEmail")}</InputLabel>
      <OutlinedInput
        value={rootUserEmail}
        id={"name"}
        sx={{ width: "100%", mb: 2 }}
        placeholder={t("field:placeholder.validMasterEmail")}
        onChange={(e) => setRootUserEmail(e.currentTarget.value)}
      />

      <InputLabel sx={{ mb: 1 }}>{t("field:label.platformLoginSuffix")}</InputLabel>
      <OutlinedInput
        value={platformLoginSuffix}
        id={"name"}
        sx={{ width: "100%", mb: 2 }}
        placeholder={t("field:placeholder.suffixIncludeCharacters")}
        onChange={(e) => setPlatformLoginSuffix(e.currentTarget.value)}
      />

      <InputLabel sx={{ mb: 1 }}>{t("field:label.platformName")}</InputLabel>
      <OutlinedInput
        value={platformName}
        id={"name"}
        sx={{ width: "100%", mb: 2 }}
        placeholder={t("field:placeholder.platformNameShownAsCustomerName")}
        onChange={(e) => setPlatformName(e.currentTarget.value)}
      />

      <InputLabel sx={{ mb: 1 }}>{t("field:label.platformBaseUrl")}</InputLabel>
      <OutlinedInput
        value={platformBaseUrl}
        id={"name"}
        sx={{ width: "100%", mb: 2 }}
        onChange={(e) => setPlatformBaseUrl(e.currentTarget.value)}
      />

      <InputLabel sx={{ mb: 1 }}>{t("field:label.collectionsToCopy")}</InputLabel>
      <Autocomplete
        value={request.collectionsToCopy}
        multiple
        onChange={(e, value) =>
          setRequest(
            produce((draft) => {
              draft.collectionsToCopy = value;
            })
          )
        }
        options={collectionToCopyList}
        sx={{ width: "100%", mb: 1 }}
        renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
      />
      <Link
        color="primary.text"
        underline="hover"
        onClick={() =>
          setRequest(
            produce((draft) => {
              draft!.collectionsToCopy = [];
            })
          )
        }
      >
        {t("button:resetList")}
      </Link>

      <InputLabel sx={{ mb: 1, mt: 2 }}>{t("field:label.userGroupsAndRoles")}</InputLabel>
      <OutlinedCheckbox
        mt={1}
        checked={request.copyUserGroups}
        onClick={() =>
          setRequest(
            produce((draft) => {
              draft.copyUserGroups = !draft.copyUserGroups;
            })
          )
        }
      >
        <Typography variant="body2" fontWeight={500}>
          {t("page:body.copyUserGroupsAndRolesFromSourcePlatform")}
        </Typography>
      </OutlinedCheckbox>

      <InputLabel sx={{ mb: 1, mt: 2 }}>{t("common:resources")}</InputLabel>
      <OutlinedCheckbox
        mt={1}
        checked={request.duplicateResources || false}
        onClick={() =>
          setRequest(
            produce((draft) => {
              draft.duplicateResources = !draft.duplicateResources;
            })
          )
        }
      >
        <Typography variant="body2" fontWeight={500}>
          {t("page:body.duplicateResources")}
          <Alert color="error" sx={{ mt: 1, fontWeight: 1, fontSize: 11, height: 33, width: "auto" }}>
            {t("alert:error.cautionWithDuplicateResourcesCheckbox")}
          </Alert>
        </Typography>
      </OutlinedCheckbox>

      <InputLabel sx={{ mb: 1, mt: 2 }}>{t("field:label.customNewPlatformId")}</InputLabel>
      <OutlinedInput
        value={request.specifiedNewPlatformId || ""}
        id={"name"}
        sx={{ width: "100%", mb: 1 }}
        type="number"
        placeholder={t("field:placeholder.letAsDefault")}
        onChange={(e) =>
          setRequest(
            produce((draft) => {
              draft!.specifiedNewPlatformId = parseInt(e.target.value);
            })
          )
        }
      />
      <Link
        color="primary.text"
        underline="hover"
        onClick={() =>
          setRequest(
            produce((draft) => {
              draft.specifiedNewPlatformId = null;
            })
          )
        }
      >
        {t("button:resetToAutoGenerateId")}
      </Link>

      <InputLabel sx={{ mb: 1, mt: 2 }}>{t("field:label.cloudProvider")}</InputLabel>
      <Autocomplete
        value={request.cloudProvider !== null ? CloudProvider[request.cloudProvider] : null}
        onChange={(e, value) =>
          setRequest(
            produce((draft) => {
              draft.cloudProvider = Object.keys(CloudProvider)[
                Object.values(CloudProvider).indexOf(value as unknown as CloudProvider)
              ] as CloudProvider;
            })
          )
        }
        options={Object.values(CloudProvider)}
        sx={{ width: "100%", mb: 1 }}
        renderInput={(params) => (
          <TextField
            sx={{ mt: "-24px" }}
            {...params}
            placeholder={t("field:placeholder.cloudResourcesWillBeCreatedAutomatically")}
          />
        )}
      />

      <InputLabel sx={{ mb: 1, mt: 2 }}>{t("field:label.billingSettings")}</InputLabel>
      <Autocomplete
        value={request.billingOfferId ? offers.find((x) => x.id == request.billingOfferId) : null}
        onChange={(e, value) =>
          setRequest(
            produce((draft) => {
              draft.billingOfferId = value ? value.id : null;
            })
          )
        }
        options={offers}
        getOptionLabel={(option) => option?.name!}
        sx={{ width: "100%", mb: 1 }}
        renderInput={(params) => (
          <TextField sx={{ mt: "-24px" }} {...params} placeholder={t("field:placeholder.selectBillingOffer")} />
        )}
      />

      <Stack direction="row" mt={4}>
        <Button
          disabled={!isValid()}
          variant="contained"
          onClick={handleValidateClick}
          startIcon={<RocketLaunchIcon />}
          loading={isSubmitLoading}
        >
          {t("button:validateAndCreatePlatform")}
        </Button>
      </Stack>
    </Stack>
  );
}

export default AdminSettingsCopyPlatform;
